import { useState } from "react";
import { useCreateHarvest, useUpdateHarvest, useDeleteHarvest } from "../hooks/useGrowsQuery.js";
import useTimezone from "../hooks/useTimezone.js";
import { formatDate } from "../utility/datetime.js";

// Rough rule of thumb: dried mushrooms are ~10% of their wet weight.
const DRY_WEIGHT_ESTIMATE_RATIO = 0.1;

function nextFlushNumber(harvests) {
  if (!harvests.length) return 1;
  return Math.max(...harvests.map((h) => h.flush_number)) + 1;
}

// Reads/writes the harvest date as a plain local calendar date, not a UTC instant —
// using toISOString() here would shift the date across a day boundary depending on
// the browser's UTC offset (e.g. Aug 11 saved would round-trip back as Aug 10).
function toDateInput(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function dateInputToISOString(dateStr) {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day).toISOString();
}

function fmtWeight(grams) {
  return grams != null ? `${grams}g` : "—";
}

function HarvestRow({ harvest, growId, tz }) {
  const updateHarvest = useUpdateHarvest(growId);
  const deleteHarvest = useDeleteHarvest(growId);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editDate, setEditDate] = useState("");
  const [editWeight, setEditWeight] = useState("");
  const [editDryWeight, setEditDryWeight] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [error, setError] = useState(null);

  function startEdit() {
    setEditDate(toDateInput(harvest.harvested_at));
    setEditWeight(harvest.weight_grams != null ? String(harvest.weight_grams) : "");
    setEditDryWeight(harvest.dry_weight_grams != null ? String(harvest.dry_weight_grams) : "");
    setEditNotes(harvest.notes ?? "");
    setError(null);
    setConfirmDelete(false);
    setEditing(true);
  }

  const wetNum = parseFloat(editWeight);
  const estimatedDry = editDryWeight === "" && !isNaN(wetNum) && wetNum > 0
    ? wetNum * DRY_WEIGHT_ESTIMATE_RATIO
    : null;

  async function saveEdit() {
    setError(null);
    const hasWeight = editWeight !== "";
    const hasDryWeight = editDryWeight !== "";
    const hasNotes = editNotes.trim() !== "";
    if (!hasWeight && !hasDryWeight && !hasNotes) {
      setError("Enter a weight or a note");
      return;
    }
    const body = {
      harvested_at: dateInputToISOString(editDate),
      weight_grams: hasWeight ? parseFloat(editWeight) : null,
      notes: hasNotes ? editNotes.trim() : null,
    };
    if (hasDryWeight) {
      body.dry_weight_grams = parseFloat(editDryWeight);
      body.is_dry_weight_estimated = false;
    } else if (estimatedDry != null) {
      body.dry_weight_grams = Math.round(estimatedDry * 10) / 10;
      body.is_dry_weight_estimated = true;
    } else {
      body.dry_weight_grams = null;
      body.is_dry_weight_estimated = false;
    }
    try {
      await updateHarvest.mutateAsync({ harvestId: harvest.id, ...body });
      setEditing(false);
    } catch {
      setError("Failed to save changes");
    }
  }

  if (editing) {
    return (
      <tr className="border-t border-gray-100 dark:border-gray-700">
        <td colSpan={6} className="py-3 px-3">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex flex-col gap-3">
            <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
              Flush {harvest.flush_number}
            </div>
            <div className="flex flex-wrap gap-3">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Date</label>
                <input
                  type="date"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                  className="border border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                />
              </div>
              <div className="flex-1 min-w-[140px]">
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Wet weight (grams)</label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={editWeight}
                  onChange={(e) => setEditWeight(e.target.value)}
                  placeholder="e.g. 142.5"
                  className="w-full border border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                />
              </div>
              <div className="flex-1 min-w-[140px]">
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Dry weight (grams)</label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={editDryWeight}
                  onChange={(e) => setEditDryWeight(e.target.value)}
                  placeholder={estimatedDry != null ? `~${estimatedDry.toFixed(1)} (estimated)` : "e.g. 14.2"}
                  className="w-full border border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                />
                {estimatedDry != null && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    Will save as ~{estimatedDry.toFixed(1)}g (estimated at 10% of wet weight) unless you enter a measured value.
                  </p>
                )}
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Notes</label>
              <input
                type="text"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="e.g. Good pins, slightly dry"
                className="w-full border border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
            {error && <div className="text-xs text-danger dark:text-danger-onDark">{error}</div>}
            <div className="flex gap-2">
              <button
                onClick={saveEdit}
                disabled={updateHarvest.isPending}
                className="text-xs bg-brand dark:bg-brand-fillOnDark text-white rounded px-3 py-1.5 hover:bg-brand-hover dark:hover:bg-brand-dark transition-colors disabled:opacity-50"
              >
                {updateHarvest.isPending ? "Saving…" : "Save"}
              </button>
              <button
                onClick={() => setEditing(false)}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-t border-gray-100 dark:border-gray-700 group">
      <td className="py-2.5 px-3 text-sm font-medium text-gray-700 dark:text-gray-200">
        Flush {harvest.flush_number}
      </td>
      <td className="py-2.5 px-3 text-sm text-gray-600 dark:text-gray-300">
        {formatDate(harvest.harvested_at, tz)}
      </td>
      <td className="py-2.5 px-3 text-sm text-gray-700 dark:text-gray-200 font-medium tabular-nums">
        {fmtWeight(harvest.weight_grams)}
      </td>
      <td className="py-2.5 px-3 text-sm text-gray-700 dark:text-gray-200 font-medium tabular-nums">
        {fmtWeight(harvest.dry_weight_grams)}
        {harvest.dry_weight_grams != null && harvest.is_dry_weight_estimated && (
          <span className="text-xs text-gray-400 dark:text-gray-500 font-normal"> (est.)</span>
        )}
      </td>
      <td className="py-2.5 px-3 text-sm text-gray-500 dark:text-gray-400">
        {harvest.notes ?? "—"}
      </td>
      <td className="py-2.5 px-3 text-right">
        {!confirmDelete ? (
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={startEdit}
              className="text-xs text-gray-400 dark:text-gray-500 hover:text-brand dark:hover:text-brand-onDark"
            >
              Edit
            </button>
            <button
              onClick={() => setConfirmDelete(true)}
              className="text-xs text-gray-400 dark:text-gray-500 hover:text-danger dark:hover:text-danger-onDark"
            >
              Delete
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">Sure?</span>
            <button
              onClick={() => deleteHarvest.mutate(harvest.id)}
              disabled={deleteHarvest.isPending}
              className="text-xs text-danger dark:text-danger-onDark hover:text-danger-hover dark:hover:text-red-300 font-medium disabled:opacity-50"
            >
              {deleteHarvest.isPending ? "…" : "Yes"}
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
            >
              No
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function HarvestLog({ growId, harvests = [] }) {
  const createHarvest = useCreateHarvest(growId);
  const tz = useTimezone();

  const [weight, setWeight] = useState("");
  const [dryWeight, setDryWeight] = useState("");
  const [notes, setNotes] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);

  const totalWeight = harvests.reduce((sum, h) => sum + (h.weight_grams ?? 0), 0);
  const totalDryWeight = harvests.reduce((sum, h) => sum + (h.dry_weight_grams ?? 0), 0);

  const wetNum = parseFloat(weight);
  const estimatedDry = dryWeight === "" && !isNaN(wetNum) && wetNum > 0
    ? wetNum * DRY_WEIGHT_ESTIMATE_RATIO
    : null;

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    const hasWeight = weight !== "";
    const hasDryWeight = dryWeight !== "";
    const hasNotes = notes.trim() !== "";
    if (!hasWeight && !hasDryWeight && !hasNotes) {
      setError("Enter a weight or a note");
      return;
    }
    const body = { flush_number: nextFlushNumber(harvests) };
    if (hasWeight) body.weight_grams = parseFloat(weight);
    if (hasNotes) body.notes = notes.trim();
    if (hasDryWeight) {
      body.dry_weight_grams = parseFloat(dryWeight);
      body.is_dry_weight_estimated = false;
    } else if (estimatedDry != null) {
      body.dry_weight_grams = Math.round(estimatedDry * 10) / 10;
      body.is_dry_weight_estimated = true;
    }
    try {
      await createHarvest.mutateAsync(body);
      setWeight("");
      setDryWeight("");
      setNotes("");
      setShowForm(false);
    } catch {
      setError("Failed to save harvest");
    }
  }

  return (
    <div className="bg-surface dark:bg-surface-dark rounded-xl shadow p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Harvest Log</h2>
          {harvests.length > 0 && (
            <div className="text-xs text-success dark:text-success-onDark mt-0.5">
              <span className="font-semibold">{harvests.length}</span> flush{harvests.length !== 1 ? "es" : ""}
              {totalWeight > 0 && ` · ${totalWeight.toFixed(1)}g wet total`}
              {totalDryWeight > 0 && <> · <span className="font-semibold">{totalDryWeight.toFixed(1)}g</span> dry total</>}
            </div>
          )}
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="text-xs bg-brand dark:bg-brand-fillOnDark text-white rounded px-3 py-1.5 hover:bg-brand-hover dark:hover:bg-brand-dark transition-colors font-medium"
          >
            + Log harvest
          </button>
        )}
      </div>

      {/* Add form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="mb-4 bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex flex-col gap-3">
          <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
            Flush {nextFlushNumber(harvests)}
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Wet weight (grams)</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="e.g. 142.5"
                className="w-full border border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Dry weight (grams)</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={dryWeight}
                onChange={(e) => setDryWeight(e.target.value)}
                placeholder={estimatedDry != null ? `~${estimatedDry.toFixed(1)} (estimated)` : "e.g. 14.2"}
                className="w-full border border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              />
              {estimatedDry != null && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Will save as ~{estimatedDry.toFixed(1)}g (estimated at 10% of wet weight) unless you enter a measured value.
                </p>
              )}
            </div>
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Notes (optional)</label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Good pins, slightly dry"
                className="w-full border border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
          </div>
          {error && <div className="text-xs text-danger dark:text-danger-onDark">{error}</div>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={createHarvest.isPending}
              className="text-xs bg-brand dark:bg-brand-fillOnDark text-white rounded px-3 py-1.5 hover:bg-brand-hover dark:hover:bg-brand-dark transition-colors disabled:opacity-50"
            >
              {createHarvest.isPending ? "Saving…" : "Save harvest"}
            </button>
            <button
              type="button"
              onClick={() => { setShowForm(false); setWeight(""); setDryWeight(""); setNotes(""); setError(null); }}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Table */}
      {harvests.length === 0 ? (
        <div className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">
          No harvests logged yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr>
                <th className="pb-2 px-3 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Flush</th>
                <th className="pb-2 px-3 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Date</th>
                <th className="pb-2 px-3 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Wet Weight</th>
                <th className="pb-2 px-3 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Dry Weight</th>
                <th className="pb-2 px-3 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Notes</th>
                <th className="pb-2 px-3" />
              </tr>
            </thead>
            <tbody>
              {harvests.map((h) => (
                <HarvestRow key={h.id} harvest={h} growId={growId} tz={tz} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
